import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

/** 仅用于展示；实际授予额度仍由计费服务核验会员订单。 */
export interface TodayWorkQuota {
  monthly_quota?: number
  quota_version?: string
  quota_policy_active?: boolean
  new_advanced_quota?: number
  new_super_quota?: number
}

const isPoints = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

/**
 * 在父窗消息或 URL 边界提取额度，缺失与零额度保留区别。
 * @param value - 钱包快照的展示字段
 * @returns 已校验的字段；不推断存量会员是否适用新规则
 * @example readTodayWorkQuota({ monthly_quota: 6000000 })
 */
export function readTodayWorkQuota(value: unknown): TodayWorkQuota {
  if (!value || typeof value !== 'object') return {}
  const raw = value as Record<string, unknown>
  const clean: TodayWorkQuota = {
    ...(isPoints(raw.monthly_quota)
      ? { monthly_quota: raw.monthly_quota }
      : {}),
    ...(typeof raw.quota_version === 'string' && raw.quota_version.length <= 96
      ? { quota_version: raw.quota_version }
      : {}),
    ...(typeof raw.quota_policy_active === 'boolean'
      ? { quota_policy_active: raw.quota_policy_active }
      : {}),
    ...(isPoints(raw.new_advanced_quota) && raw.new_advanced_quota > 0
      ? { new_advanced_quota: raw.new_advanced_quota }
      : {}),
    ...(isPoints(raw.new_super_quota) && raw.new_super_quota > 0
      ? { new_super_quota: raw.new_super_quota }
      : {})
  }
  // 无效的新字段与完全不支持新协议的旧服务必须区分。
  if (
    Object.keys(clean).length === 0 &&
    [
      'monthly_quota',
      'quota_version',
      'quota_policy_active',
      'new_advanced_quota',
      'new_super_quota'
    ].some((key) => raw[key] !== undefined)
  )
    clean.quota_version = ''
  return clean
}

/**
 * 新开、续费和升级使用新售卖规则；不会用它代替当前会员月额度。
 * @param quota - 客户端钱包快照
 * @param tier - 将购买的会员档位
 * @returns 新购额度；新策略字段不完整时返回未知
 * @example newPurchaseQuota({}, 'advanced') // undefined (不能凭空承诺旧额度)
 */
export function newPurchaseQuota(
  quota: TodayWorkQuota | undefined,
  tier: 'advanced' | 'super'
): number | undefined {
  if (!quota) return undefined
  const clean = readTodayWorkQuota(quota)
  if (
    clean.quota_version?.trim() &&
    clean.new_advanced_quota !== undefined &&
    clean.new_super_quota !== undefined
  )
    return tier === 'advanced'
      ? clean.new_advanced_quota
      : clean.new_super_quota
  return undefined
}

/**
 * 从新版客户端专用收银台参数读取快照；普通网页和旧客户端保持原行为。
 * @param params - 收银台 URL 参数
 * @returns 客户端快照，或非新版客户端的 undefined
 * @example quotaFromPaywallParams(new URLSearchParams('tw_quota_context=1'))
 */
export function quotaFromPaywallParams(
  params: URLSearchParams
): TodayWorkQuota | undefined {
  if (params.get('tw_quota_context') !== '1') return undefined
  const numeric = (key: string) => {
    const raw = params.get(key)
    return raw !== null && /^\d{1,16}$/.test(raw) ? Number(raw) : undefined
  }
  return readTodayWorkQuota({
    monthly_quota: numeric('monthly_quota'),
    quota_version: params.get('quota_version') ?? undefined,
    quota_policy_active:
      params.get('quota_policy_active') === 'true'
        ? true
        : params.get('quota_policy_active') === 'false'
          ? false
          : undefined,
    new_advanced_quota: numeric('new_advanced_quota'),
    new_super_quota: numeric('new_super_quota')
  })
}

/**
 * 格式化展示额度，未知不会变成零。
 * @param points - 可信的积分额度
 * @returns 积分数量或未知说明
 * @example formatQuotaPoints(3000000) // '300万'
 */
export function formatQuotaPoints(points: number | undefined): string {
  if (!isPoints(points)) return '以实际权益为准'
  return points >= 10000
    ? `${Number((points / 10000).toFixed(2))}万`
    : String(points)
}

/** 新购月度额度，字段缺失时不把“以实际权益为准”拼成数字。 */
export function formatNewPurchaseMonthly(
  quota: TodayWorkQuota | undefined,
  tier: 'advanced' | 'super'
): string {
  const points = newPurchaseQuota(quota, tier)
  return points === undefined
    ? '以实际权益为准'
    : `${formatQuotaPoints(points)} 积分/月`
}

/** 从当前团队钱包读取售卖口径；快照按团队隔离，接口不可用时保持未知。 */
export function useWalletQuota(
  visible: boolean,
  groupId: string,
  suppliedQuota?: TodayWorkQuota
): TodayWorkQuota | undefined {
  const [snapshot, setSnapshot] = useState<{
    groupId: string
    activation: number
    quota: TodayWorkQuota
  } | null>(null)
  const activationRef = useRef({ visible: false, serial: 0 })
  if (visible && !activationRef.current.visible) activationRef.current.serial++
  activationRef.current.visible = visible
  const activation = activationRef.current.serial

  useEffect(() => {
    if (!visible || !groupId || suppliedQuota !== undefined) return
    const controller = new AbortController()
    fetch(
      `${__API_SERVER_HOST__}/web/agent/wallet?group_id=${encodeURIComponent(groupId)}`,
      { credentials: 'include', signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(`wallet ${response.status}`)
        return response.json()
      })
      .then((body) => {
        if (controller.signal.aborted || body?.code !== 200) return
        setSnapshot({
          groupId,
          activation,
          quota: readTodayWorkQuota(body.data)
        })
      })
      .catch(() => {
        // 钱包不可用时不能回退成旧积分承诺。
      })
    return () => controller.abort()
  }, [visible, groupId, suppliedQuota, activation])

  return (
    suppliedQuota ??
    (visible &&
    snapshot?.groupId === groupId &&
    snapshot.activation === activation
      ? snapshot.quota
      : undefined)
  )
}

let currentQuota: TodayWorkQuota | undefined
const listeners = new Set<() => void>()

/**
 * 由已校验账号和团队的专用权益页桥发布展示快照。
 * @param quota - 当前快照；卸载或身份变化时清除
 * @returns 无返回值
 * @example publishTodayWorkQuota(undefined)
 */
export function publishTodayWorkQuota(quota: TodayWorkQuota | undefined): void {
  if (JSON.stringify(currentQuota) === JSON.stringify(quota)) return
  currentQuota = quota
  listeners.forEach((notify) => notify())
}

/** 权益对比页读取已完成宿主握手的快照；普通网页返回 undefined。 */
export function useTodayWorkQuota(): TodayWorkQuota | undefined {
  return useSyncExternalStore(
    (notify) => {
      listeners.add(notify)
      return () => {
        listeners.delete(notify)
      }
    },
    () => currentQuota,
    () => undefined
  )
}
